import Add16 from '@carbon/icons-react/es/add/16';
import ArrowsHorizontal32 from '@carbon/icons-react/es/arrows--horizontal/32';
import Enterprise32 from '@carbon/icons-react/es/enterprise/32';
import Partnership16 from '@carbon/icons-react/es/partnership/16';
import { FormInstance, Skeleton, Space } from 'antd';
import { isEmpty } from 'lodash';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Avatar, AvatarGroup } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import {
  AvatarGroupSkeleton,
  UserDetailsSkeleton,
} from 'components/atoms/custom-skeletons';
import { AvatarWithDetails } from 'components/molecules/avatar-with-details';
import { ProjectDuration } from 'components/molecules/project-duration';
import { Label01, ProductiveHeading05 } from 'components/styles';
import { useUser } from 'hooks/use-user';
import { useCompanyLogoQuery } from 'queries';
import { Logo, LogosContainer } from './logo';
import { useManagePartners } from './manage-partner';
import { PartnerRow, Partners } from './partner-row';
import { PartnerSelect } from './partner-select';
import { ProjectData } from './types';
import { VendorTeamStepFormValues } from './vendor-team-step';

const PreviewContainer = styled.div`
  width: 312px;
  height: 100%;
  overflow-y: auto;
  padding: 0 var(--spacing-06);
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--white-100);
  padding: 0 var(--spacing-03);
`;

const Content = styled.div`
  margin-top: var(--spacing-09);
  padding: 0 var(--spacing-03);
`;

const AddPartnerButton = styled(Button)`
  width: 100%;
  margin-top: var(--spacing-05);
  justify-content: space-between;
`;

const PreviewElementWrapper = styled.div`
  margin-bottom: var(--layout-03);
  &:last-child {
    margin-bottom: 0;
  }
`;

const ProjectDurationContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled.p`
  ${Label01};
`;

const ProjectName = styled.h3`
  ${ProductiveHeading05};
`;

interface Props {
  projectData: ProjectData;
  onValuesChange: (values: any, allValues: unknown) => void;
  vendorStepform: FormInstance<VendorTeamStepFormValues>;
}

const getDomain = (emailId = ''): string => {
  return emailId.split('@')[1] ?? '';
};

const getCustomerChampionEmailId = ({
  customerChampion,
}: Pick<ProjectData, 'customerChampion'>): string | undefined => {
  return customerChampion?.__isNew__ ? customerChampion.value : undefined;
};

const isNewCustomer = ({
  customer,
}: Pick<ProjectData, 'customer'>): boolean | undefined => {
  return customer && customer.__isNew__;
};

export const Preview: FC<Props> = ({
  projectData,
  onValuesChange,
  vendorStepform,
}) => {
  const { company } = useUser();
  const {
    customer,
    projectName,
    projectOwner,
    startDate,
    dueDate,
    internalUsers,
    externalUsers,
    partners,
  } = projectData;

  const { data: companyLogoResult } = useCompanyLogoQuery({
    companyDomain: isNewCustomer(projectData)
      ? getDomain(getCustomerChampionEmailId(projectData))
      : '',
  });
  const {
    partnerOptions,
    partnersAvailable,
    partnersLoading,
    addPartners,
    removePartner,
  } = useManagePartners({
    projectData,
    onValuesChange,
    vendorStepform,
  });

  return (
    <PreviewContainer>
      <Header>
        <PreviewElementWrapper>
          <LogosContainer>
            <Logo
              src={
                isNewCustomer(projectData)
                  ? companyLogoResult?.companyLogoUrl
                  : customer?.imageUrl
              }
              name={customer?.label}
              size={88}
              icon={<Enterprise32 />}
            />
            <ArrowsHorizontal32 />
            <Logo
              src={company.companyLogoUrl}
              name={company.companyName}
              size={88}
              icon={<Enterprise32 />}
            />
          </LogosContainer>
        </PreviewElementWrapper>
        <PreviewElementWrapper>
          {projectName ? (
            <ProjectName>{projectName}</ProjectName>
          ) : (
            <Skeleton.Input style={{ width: 200, height: 16 }} size="small" />
          )}

          {customer?.label ? (
            <Label>{customer?.label}</Label>
          ) : (
            <Skeleton.Input
              style={{ width: 200, height: 12, marginTop: 4, marginBottom: 16 }}
              size="small"
            />
          )}

          <ProjectDurationContainer>
            <ProjectDuration startDate={startDate} dueDate={dueDate} />
          </ProjectDurationContainer>
          {!partnersAvailable ? null : (
            <PartnerSelect
              options={partnerOptions}
              onChange={addPartners}
              selected={partners ?? []}
              placement="rightTop"
              triggerComponent={
                <AddPartnerButton
                  type="tertiary"
                  icon={
                    <span>
                      <Add16 />
                      <Partnership16 />
                    </span>
                  }
                  iconPlacement="right"
                  loading={partnersLoading}
                >
                  <FormattedMessage id="projects.create.team_details.add_partner" />
                </AddPartnerButton>
              }
            />
          )}
        </PreviewElementWrapper>
      </Header>
      <Content>
        {!partnersAvailable ? null : (
          <PreviewElementWrapper>
            <Label>
              <FormattedMessage id="partners" />
            </Label>
            {!isEmpty(partners) ? (
              <Partners>
                {partners?.map((o) => (
                  <PartnerRow partnerOption={o} onRemove={removePartner} />
                ))}
              </Partners>
            ) : (
              <Space size={12}>
                <Skeleton.Avatar size="large" />
                <Skeleton.Input
                  style={{ width: 156, height: 16, marginTop: 2 }}
                  size="small"
                />
              </Space>
            )}
          </PreviewElementWrapper>
        )}

        {/* Project Owner */}
        <PreviewElementWrapper>
          <Label>
            <FormattedMessage id="projects.list.project_owner" />
          </Label>
          {projectOwner ? (
            <AvatarWithDetails
              src={projectOwner.imageUrl}
              title={projectOwner.label}
            />
          ) : (
            <UserDetailsSkeleton />
          )}
        </PreviewElementWrapper>

        {/* Internal/native Team */}
        <PreviewElementWrapper>
          <Label>
            <FormattedMessage
              id="projects.create.team_details.team_members"
              values={{
                companyName: company.companyName,
              }}
            />
          </Label>

          {!isEmpty(internalUsers) ? (
            <AvatarGroup maxCount={4}>
              {internalUsers?.map((u) => (
                <Avatar key={u.value} src={u.imageUrl} name={u.label} />
              ))}
            </AvatarGroup>
          ) : (
            <AvatarGroupSkeleton />
          )}
        </PreviewElementWrapper>

        {/* Customer/External team */}
        <PreviewElementWrapper>
          <Label>
            <FormattedMessage
              id="projects.create.team_details.external_team_members"
              values={{
                companyName: customer?.label || 'Customer',
              }}
            />
          </Label>

          {!isEmpty(externalUsers) ? (
            <AvatarGroup maxCount={4}>
              {externalUsers?.map((o) => (
                <Avatar key={o.value} src={o.imageUrl} name={o.label} />
              ))}
            </AvatarGroup>
          ) : (
            <AvatarGroupSkeleton />
          )}
        </PreviewElementWrapper>
      </Content>
    </PreviewContainer>
  );
};
